// CADENCE-1, HER CLAUSE 5 — **THE CADENCE LINE IN ANALYTICS: IN SEQUENCE, SENT, TAPPED, SIGNED IN,
// CONVERTED.** Her five figures, in her order, on the tab she reads to decide advertising spend.
//
// >>> IT IS HOOKLESS AND TAKES ITS DATA AS A PROP, SO A GATE CALLS IT <<< (R7/R12's entry
// requirement, and `OwnerTraffic` / `OwnerLadder` / `OwnerPushDelivery`'s own shape on this same
// tab). The analytics tab reads ONE payload for the whole tab, so a fetch here would be a second
// answer to *what period is this* on a dashboard whose whole D6-3 ruling is one range control.
//
// >>> EVERY FIGURE AND EVERY LABEL IS `src/cadence.js`'s, IMPORTED. <<< `CADENCE_ROWS` is read by
// the store's scoreboard AND by this surface, so the number the query counts and the number on her
// screen cannot be two answers (R28/R29) — which on a machine she judges spend by is the whole
// point of it. Nothing here re-derives a count, re-orders the row or invents a label.
//
// >>> AND THE TWO KINDS OF FIGURE ARE SAID APART, WHICH IS R18 ON A ROW OF NUMBERS. <<<
// `inSequence` is a fact about RIGHT NOW — how many leads the machine is currently working — and
// the other four are facts about the WINDOW her picker resolved. `membershipCensus` one tab over
// already carries this exact split with its reason written down. Drawn silently side by side she
// would read all five as the period's, and *four people converted this week* beside *1,312 are in
// sequence* is a rate she would compute out of two different questions.
import { CADENCE_ROWS, cadenceFigure } from "../cadence.js";

/** What the store's `cadenceScoreboard` answers: her five keys, each a number or `null`. */
export type CadencePayload = Record<string, number | null>;

/** Standing rather than ranged, by key. The one that is a fact about right now says so. */
const STANDING = new Set(["inSequence"]);

const short = (n: number) => new Intl.NumberFormat("en-US").format(n);

export function OwnerCadence({
  cadence, rangeLabel,
}: {
  cadence: CadencePayload | null | undefined; rangeLabel: string;
}) {
  // >>> THREE STATES, NEVER TWO (R18/R65), AND THE MIDDLE ONE IS THE EXPENSIVE ONE. <<<
  // `undefined` is a payload still in flight and HOLDS STILL. `null` is a read that ARRIVED AND
  // COULD NOT BE MADE, and it says so — drawing it as five zeroes puts *the cadence reached nobody
  // and converted nobody* on the screen she judges the machine by, on a day the database refused a
  // query. A scoreboard of real zeroes is an ANSWER and is allowed to draw as one.
  if (cadence === undefined) return null;
  if (cadence === null) {
    return (
      <section className="owner-cadence" data-cadence-state="unread">
        <h3 className="owner-detail__sub">The cliffhanger cadence</h3>
        <p className="owner-page__note">These numbers could not be read just now.</p>
      </section>
    );
  }

  return (
    <section className="owner-cadence" data-cadence-state="read">
      <h3 className="owner-detail__sub">The cliffhanger cadence</h3>
      <div className="owner-cadence__row">
        {CADENCE_ROWS.map((row) => {
          // The LAW decides what a figure is, never this surface: a value that is not a real finite
          // number answers `null` and draws a dash. `Number(true)` is 1 and `Number("")` is 0
          // (R53/R136/R137), and either would put a fabricated conversion on the row she reads.
          const n = cadenceFigure(cadence[row.key]);
          return (
            <div className="owner-cadence__step" key={row.key} data-cadence-figure={row.key}>
              {/* A figure that could not be read is a DASH rather than a zero. On this row the two
                  are the difference between *nobody tapped* and *we could not count*, and only one
                  of them is a fact about her members. */}
              <span className="owner-cadence__n">{n === null ? "—" : short(n)}</span>
              <span className="owner-cadence__k">{row.label}</span>
              <span className="owner-cadence__when">
                {STANDING.has(row.key) ? "right now" : rangeLabel}
              </span>
            </div>
          );
        })}
      </div>
      {/* >>> WHAT THIS ROW DOES NOT CLAIM, ON THE ROW ITSELF (R17/R160 — no silent caps). <<<
          `sent` is what this house handed the rail; **the word DELIVERED appears nowhere on this
          schema or this surface**, because the house knows what it gave SendGrid and nothing about
          what an inbox did with it (R141/DT-1b). Saying so here is what stops *Emails sent* being
          read as *Emails read*, which is the number a spending decision would be made out of. */}
      <p className="owner-cadence__note">
        Sent counts what the house handed the mail rail, not what reached an inbox. Tapped and signed
        back in are counted from the links in those emails.
      </p>
    </section>
  );
}
